/**
 * CustomerDashboard.js - Phase E4
 * Customer account dashboard
 *
 * Features:
 * - Overview with quick stats
 * - Bookings management
 * - Order history
 * - Package credits tracking
 * - Profile settings
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PackageCard from "../../components/PackageCard";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import SlotConflictPanel from "../../components/SlotConflictPanel";
// THE ADDRESS FIELD (2026-10-23) — the user's rule: any form that takes an address suggests it.
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import { keepSlotConflictTime } from "../../utils/slotConflictApi";
import { reservePaymentTab, sendReservedTabToPayment } from "../../utils/paymentTab";
import { buildPackageBookingLink } from "../../utils/packageBookingLink";
import {
  formatCurrency,
  formatDate as formatDateTz,
  formatTime as formatTimeTz,
} from "../../utils/displayHelpers";
import {
  NON_REFUNDABLE_PROCESSING_FEE_NOTICE,
  NON_REFUNDABLE_PROCESSING_FEE_REFUND_NOTE,
} from "../../utils/transactionPolicyText";
// The fee bands MUST come from the live policy, not a hardcoded ladder: the server
// charges what `resolveCancellationFeePercent` mirrors, and nothing else.
import {
  resolveCancellationFeePercent,
  describeCancellationPolicy,
} from "../../utils/cancellationPolicyMirror";
import "./CustomerDashboard.css";

/**
 * IS THIS PURCHASE OUT OF TIME? The DATE decides, not the stored status: the sweep that flips `status` runs
 * nightly, so between a package running out and the next sweep the status still says `active`. Anything that
 * shows what is usable must therefore read the date (reported 2026-10-19: an expired package still claimed
 * "Sessions remaining: 4 / 4" and 100%).
 */
const isPurchaseExpired = (purchase) => {
  const until = purchase?.validUntil ? new Date(purchase.validUntil) : null;
  return Boolean(until && !Number.isNaN(until.getTime()) && until.getTime() < Date.now());
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Filter states
  const [bookingFilter, setBookingFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelBookingData, setCancelBookingData] = useState(null); // For cancellation modal

  // Group cancellation scope. A group booking is N bookings paid with ONE Stripe
  // payment, so cancelling any of them is one refund - the customer chooses WHICH
  // bookings are being cancelled instead of always losing the whole appointment.
  // Selecting the anchor cancels all of them (enforced by the server too).
  const [cancelTargets, setCancelTargets] = useState([]);
  const [cancelSelection, setCancelSelection] = useState([]);
  /**
   * 22G — WHAT TO DO WITH THE PACKAGE SESSIONS THIS CANCELLATION LOSES.
   *
   * `refund` is the DEFAULT, and deliberately: it is exactly what a card booking does, so a customer who does
   * not read carefully still gets the money back. `redeem` is the deliberate choice — pay the shop's fee and the
   * sessions return to the package. The two are mutually exclusive and the choice is final, because taking both
   * would recover more than the sessions were worth.
   */
  /**
   * KEEP OR LOSE THE SESSIONS — and it starts UNSELECTED (user, 2026-10-17): *"its important to let them
   * intentionally make this selection to avoid any issues arising from I wasn't aware my sessions will be
   * lost"*. The old default was "refund", which meant a customer who never read the block had already chosen the
   * outcome that loses the sessions. Now nothing is chosen until they choose it, and the Cancel button stays
   * disabled until they do.
   */
  const [cancelPackageChoice, setCancelPackageChoice] = useState("");
  /**
   * 22G — PAY NOW OR PAY LATER (user, 2026-10-14: *"we should have a sub-option for if they want to pay now or
   * later as well"*).
   *
   * `now` opens the payment window as the cancellation completes; `later` leaves the held sessions on the booking
   * page, where the same offer waits with its price. Nothing is lost by waiting — that is the whole point, and
   * saying so is what makes it a real choice rather than a warning.
   */
  const [cancelPayTiming, setCancelPayTiming] = useState("now");
  /**
   * WHICH HELD SESSIONS THIS PAYMENT IS FOR (user, 2026-10-16): `this` = the sessions this cancellation loses,
   * `all` = those plus everything else already held on the package. Both figures are shown before they choose,
   * and the ids that were SHOWN are the ids that get priced, so the charge matches the figure either way.
   */
  const [cancelPayScope, setCancelPayScope] = useState("this");
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  // False until the live policy has been obtained. Without this the preview would
  // render "no fee" while the policy was still loading (or after the request failed)
  // and then flip to a real fee - understating what the server will charge.
  const [cancellationPolicyReady, setCancellationPolicyReady] = useState(false);
  // Part B: slot conflict acknowledgement ("Keep this time"). Tracked per booking
  // so one card's spinner or error cannot leak onto another.
  const [keepingSlotConflictId, setKeepingSlotConflictId] = useState(null);
  const [slotConflictError, setSlotConflictError] = useState("");
  const [slotConflictErrorBookingId, setSlotConflictErrorBookingId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null); // For expandable order details
  const [expandedOrderData, setExpandedOrderData] = useState(null); // Full order details
  const [historyClaimPreview, setHistoryClaimPreview] = useState(null);
  const [showHistoryClaimPrompt, setShowHistoryClaimPrompt] = useState(false);
  const [historyClaimLoading, setHistoryClaimLoading] = useState(false);
  const [historyClaimError, setHistoryClaimError] = useState("");

  // Profile edit states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    billingAddress: {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
  });
  // Handle responsive resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update active tab when URL changes
  useEffect(() => {
    if (
      tabFromUrl &&
      ["overview", "bookings", "orders", "packages", "profile"].includes(
        tabFromUrl,
      )
    ) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Fetch full data when tab changes
  useEffect(() => {
    if (activeTab === "bookings") {
      fetchAllBookings();
    } else if (activeTab === "orders") {
      fetchAllOrders();
    } else if (activeTab === "packages") {
      fetchAllPackages();
    } else if (activeTab === "profile") {
      fetchHistoryClaimPreview();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDashboardData();
    fetchCancellationPolicy();
  }, []);

  /**
   * ── BACK FROM PAYING A REACTIVATION (user, 2026-10-19) ─────────────────────────────────────────
   *
   * *"After paying the reactivation amount, it should send the customer back to at least the dashboard."* It
   * does — the checkout's success_url lands here — and this settles it. The return page was the settlement rail
   * before, so landing on the dashboard without this would leave the package expired until the webhook arrived
   * (and a local `stripe listen` that is not running means it never does). Same style as the redemption: the
   * browser is holding proof of payment, so it acts on it.
   *
   * Idempotent because the server is: `reactivatePackagePurchase` writes only while the reactivation count still
   * holds the value it decided on.
   */
  useEffect(() => {
    if (searchParams.get("reactivated") !== "1") return;
    const sessionId = searchParams.get("session_id");
    const purchaseId = searchParams.get("purchase_id");
    if (!sessionId || !purchaseId) return;

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("ogestyle_token");
        const response = await fetch(
          // THE ROUTE IS /:purchaseId/confirm, not /purchase/:purchaseId/confirm — the wrong path 404'd, which is
          // the "we could not confirm the reactivation" the customer saw AFTER being charged (reported 2026-10-19).
          `${process.env.REACT_APP_API_BASE_URL}/api/packages/${purchaseId}/confirm?session_id=${encodeURIComponent(sessionId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await response.json().catch(() => ({}));
        if (!cancelled) {
          alert(
            data.message ||
              (response.ok
                ? "Your package is active again."
                : "We could not confirm the reactivation. If you were charged, it will be applied shortly."),
          );
          setActiveTab("packages");
          await fetchAllPackages();
          // "REACTIVATE AND BOOK" finishes where it said it would: on the shop that sold the package.
          const bookAfterPayment = sessionStorage.getItem(
            "package_reactivate_then_book",
          );
          if (bookAfterPayment && response.ok) {
            sessionStorage.removeItem("package_reactivate_then_book");
            navigate(`/business/${bookAfterPayment}`, { replace: true });
            return;
          }
          // Clear the query so a refresh does not replay the confirmation.
          navigate("/customer/dashboard?tab=packages", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          alert("We could not confirm the reactivation. If you were charged, it will be applied shortly.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistoryClaimPreview = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers/history-claim/preview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      setHistoryClaimPreview(data);
      setShowHistoryClaimPrompt(Boolean(data?.showPrompt));
    } catch (err) {
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login?redirect=/customer/dashboard");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login?redirect=/customer/dashboard");
          return;
        }
        throw new Error("Failed to load dashboard data");
      }

      const data = await response.json();

      // Debug logging to verify correct user and data
      setDashboardData(data);
      // Keep bookings tab data sourced from /api/bookings/my-bookings,
      // which includes full populated staff/shop/service references.
      setOrders(data.recentOrders || []);
      setPackages(data.activePackages || []);
      await fetchHistoryClaimPreview();

      // Set profile form data
      if (data.customer) {
        setProfileForm({
          firstName: data.customer.firstName || "",
          lastName: data.customer.lastName || "",
          email: data.customer.email || "",
          phone: data.customer.phone || "",
          billingAddress: data.customer.billingAddress || {
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          },
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBookings = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/my-bookings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      } else {
      }
    } catch (err) {
    }
  };

  const fetchAllOrders = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/customer/orders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setOrders(data || []);
      } else {
      }
    } catch (err) {
    }
  };

  const fetchAllPackages = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/customer/purchases?status=active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setPackages(data.purchases || []);
      } else {
      }
    } catch (err) {
    }
  };

  /**
   * ── PHASE 10 — REACTIVATING AN EXPIRED PACKAGE ─────────────────────────────────────────────────
   *
   * The FEE and the NEXT WINDOW come from the server (`purchase.renewal`), and the server charges exactly what it
   * quoted — the browser never does the ladder arithmetic. A package with nothing left costs nothing and is
   * applied immediately (no payment page for $0.00); otherwise Stripe opens and the return page settles it, with
   * the webhook as the second rail.
   */
  const [reactivatingId, setReactivatingId] = useState("");
  const reactivatePackage = async (purchase, { thenBook = false } = {}) => {
    if (!purchase?._id || reactivatingId) return;
    const businessSlug = purchase.shopId?.businessId?.slug;
    // "REACTIVATE AND BOOK": remember where they were going, because the payment leaves this page. The
    // settlement effect reads it back and lands them on the shop instead of back on the dashboard.
    if (thenBook && businessSlug) {
      sessionStorage.setItem("package_reactivate_then_book", businessSlug);
    } else {
      sessionStorage.removeItem("package_reactivate_then_book");
    }
    setReactivatingId(purchase._id);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/purchase/${purchase._id}/reactivate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        alert(data.message || "This package could not be reactivated.");
        return;
      }
      if (data.stripeCheckoutUrl) {
        // SAME TAB (user, 2026-10-19). A new tab left the dashboard behind on the old tab and the customer with
        // two of them; the checkout returns to /customer/dashboard?reactivated=1, which is this page, so
        // navigating away is safe and the return lands exactly where they started.
        window.location.assign(data.stripeCheckoutUrl);
        return;
      }
      alert(
        data.validUntil
          ? `Your package is active again until ${new Date(data.validUntil).toLocaleDateString()}.`
          : data.message || "Your package is active again.",
      );
      await fetchAllPackages();
      // A FREE reactivation never leaves the page, so the booking intent is honoured here.
      const bookAfter = sessionStorage.getItem("package_reactivate_then_book");
      if (bookAfter) {
        sessionStorage.removeItem("package_reactivate_then_book");
        navigate(`/business/${bookAfter}`);
      }
    } catch (err) {
      alert("This package could not be reactivated. Please try again.");
    } finally {
      setReactivatingId("");
    }
  };

  const loadBookingDetails = async (booking) => {
    if (!booking?._id) return booking;

    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) return booking;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${booking._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        return booking;
      }

      const data = await response.json();
      // 22G — the SERVER's package warning travels with the booking (`packageCancellation`), plus the same
      // figures for every booking that could be cancelled with it (`packageCancellations`, keyed by id). It is
      // not mirrored here on purpose: these are money figures, and a second implementation of them is how a
      // screen comes to promise a number the charge does not honour.
      return {
        ...(data?.booking || booking),
        packageCancellation: data?.packageCancellation || null,
        packageCancellations: data?.packageCancellations || {},
      };
    } catch (err) {
      return booking;
    }
  };

  const fetchCancellationPolicy = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/cancellation-policy`,
      );
      if (response.ok) {
        const data = await response.json();
        setCancellationPolicy(data.policy || null);
        // A null policy here is authoritative (the platform really has none, and the
        // server charges nothing), unlike "we could not ask".
        setCancellationPolicyReady(true);
      }
    } catch (err) {
      // Leave cancellationPolicyReady false: the preview then declines to state a
      // fee rather than guessing zero.
    }
  };

  /**
   * Every still-live booking that shares this booking's group, anchor first.
   *
   * `/api/bookings/my-bookings` already returns one document per group member, so
   * this needs no extra request - and the ids it yields are the ids the cancel API
   * expects. Anchor first because it is the one row whose selection changes the
   * meaning of the whole action, so it must never be buried between members.
   */
  const getGroupCancelTargets = useCallback(
    (booking) => {
      if (!booking) return [];
      const groupId = booking.groupBookingId
        ? String(booking.groupBookingId)
        : null;

      const live = (groupId
        ? bookings.filter(
            (candidate) =>
              String(candidate.groupBookingId || "") === groupId &&
              !["cancelled", "no-show"].includes(candidate.status),
          )
        : [booking]
      ).slice();

      return live.sort((a, b) => {
        const aAnchor = a.isPrimaryGroupBooking === true;
        const bAnchor = b.isPrimaryGroupBooking === true;
        if (aAnchor !== bAnchor) return aAnchor ? -1 : 1;
        return String(a.startTime || "").localeCompare(String(b.startTime || ""));
      });
    },
    [bookings],
  );

  // NOTE: the refund preview memos live further down this component, immediately after
  // getBookingDateTime/getBookingServicePrice. A useMemo body runs during render, so
  // placing them here would call helpers that are still in the temporal dead zone.

  // Part B: acknowledge the time we held after the customer's slot was taken
  // mid-payment. The appointment does not move - this just records their answer.
  const handleKeepSlotConflictTime = async (booking) => {
    const bookingId = booking?._id;
    if (!bookingId) return;

    setKeepingSlotConflictId(bookingId);
    setSlotConflictError("");
    setSlotConflictErrorBookingId(null);

    try {
      const updatedBooking = await keepSlotConflictTime(bookingId);

      setBookings((previous) =>
        previous.map((entry) =>
          entry._id === bookingId
            ? {
                ...entry,
                ...updatedBooking,
                slotConflict: updatedBooking?.slotConflict || entry.slotConflict,
              }
            : entry,
        ),
      );
    } catch (err) {
      setSlotConflictError(err.message || "Unable to keep this time");
      setSlotConflictErrorBookingId(bookingId);
    } finally {
      setKeepingSlotConflictId(null);
    }
  };

  const handleCancelBooking = async (booking) => {
    const fullBooking = await loadBookingDetails(booking);
    const targets = getGroupCancelTargets(fullBooking);

    // ── THE PACKAGE WARNING MUST TRAVEL WITH THE MODAL, NOT JUST WITH THE BOOKING ─────────────────
    //
    // `loadBookingDetails` returns it from the server's `packageCancellation` block, and the dialog reads it from
    // HERE. Making it a sibling of `booking` rather than a property on it keeps the booking document the booking
    // document — and it is why the dialog can show the two figures BEFORE the customer commits to either.
    setCancelBookingData({
      booking: fullBooking,
      packageCancellation: fullBooking.packageCancellation || null,
      // Every booking the dialog can tick, each priced by the server. The screen only ADDS them — the
      // policy, the shop's tiers and the frozen snapshot stay on the server.
      packageCancellations: fullBooking.packageCancellations || {},
    });
    // NOTHING IS PRE-CHOSEN. The dialog used to start on "refund", which is the outcome that LOSES the sessions;
    // a customer who never read the block had silently chosen it (user, 2026-10-17: *"its important to let them
    // intentionally make this selection to avoid any issues arising from I wasn't aware my sessions will be
    // lost"*). The radios start blank and the confirm button stays disabled until one is picked.
    setCancelPackageChoice("");
    setCancelTargets(targets);
    // Pre-tick the booking that was clicked. Clicking the anchor pre-ticks the whole
    // group, which is exactly what the server will do with an anchor selection.
    setCancelSelection(
      fullBooking.isPrimaryGroupBooking
        ? targets.map((target) => target._id)
        : [fullBooking._id],
    );
  };

  const handleCancelTargetToggle = (target) => {
    const isAnchor = target.isPrimaryGroupBooking === true;

    setCancelSelection((previous) => {
      if (isAnchor) {
        // Ticking the anchor ticks everything - members cannot stay booked once the
        // anchor is gone (their times are chained off it).
        return previous.includes(target._id)
          ? previous.filter((id) => id !== target._id)
          : cancelTargets.map((entry) => entry._id);
      }
      return previous.includes(target._id)
        ? previous.filter((id) => id !== target._id)
        : [...previous, target._id];
    });
  };

  /**
   * 22G — PAY TO GET LOST PACKAGE SESSIONS BACK.
   *
   * Opens Stripe in a NEW TAB so the customer keeps the page they were on (their own request: *"if possible open
   * the stripe payment page in a new tab and after payment close it and silently update the session to be
   * usable"*). The tab that comes back closes itself; this page just refreshes its data, because the SESSIONS
   * ARE RETURNED BY THE SERVER, not by the browser coming back.
   *
   * THE TAB IS RESERVED BEFORE THE REQUEST — see `reservePaymentTab`. `window.open` after an `await` is blocked,
   * and the old fallback navigated THIS tab away, which lost the dashboard the customer was on. If a blocker
   * refuses even a reserved window, the link is offered instead of followed.
   */
  const startPackageRedemption = async (
    purchaseId,
    reservedTab = null,
    providedToken = null,
    holdEntryIds = null,
  ) => {
    try {
      // The cancellation's own token first, then the session: a package bought while logged out carries
      // `customerId: null`, so a session cannot redeem it even for the person who paid for it.
      const token = providedToken || localStorage.getItem("ogestyle_token");
      if (!token || !purchaseId) {
        // NEVER SILENT. This used to close the tab and return, so a customer who chose "pay now" got nothing at
        // all — no tab, no message, and no idea whether the payment had happened (reported 2026-10-16).
        if (reservedTab) reservedTab.close();
        alert(
          !token
            ? "Session expired. Please sign in again to pay for the lost sessions — they stay held for you."
            : "Your sessions are held, but we could not tell which package to pay for. Please open this booking again and use the “Pay to get them back” button there.",
        );
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/purchase/${purchaseId}/redeem-held`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // THE HOLDS THIS CANCELLATION CREATED, so the charge matches the figure the dialog showed. Without them
          // the route prices every held session on the package: a cancellation quoted at $40 took the customer to
          // a payment for $80 (reported 2026-10-16).
          body: JSON.stringify(
            Array.isArray(holdEntryIds) && holdEntryIds.length > 0 ? { holdEntryIds } : {},
          ),
        },
      );
      const data = await response.json();

      if (!response.ok || !data?.stripeCheckoutUrl) {
        if (reservedTab) reservedTab.close();
        alert(data?.message || "Could not open the payment page.");
        return;
      }

      const url = String(data.stripeCheckoutUrl);
      // Stripe's own domain or nothing: a tampered response must not become an open redirect.
      if (!/\.stripe\.com\//.test(url)) {
        if (reservedTab) reservedTab.close();
        alert("Could not open the payment page.");
        return;
      }

      if (!sendReservedTabToPayment(reservedTab, url)) {
        // OFFERED, never followed: navigating this tab is what lost the page they were on.
        alert(
          `Your browser blocked the payment window. Open this link to pay:\n\n${url}\n\nThis page stays as it is, and your sessions return as soon as you have paid.`,
        );
      }
    } catch (err) {
      if (reservedTab) {
        try {
          reservedTab.close();
        } catch {
          // Nothing to do.
        }
      }
      alert("Could not open the payment page.");
    }
  };

  const confirmCancellation = async () => {
    // THE PAYMENT TAB IS RESERVED BEFORE THE FIRST `await` — reserving it afterwards is what a browser blocks, and
    // the fallback then navigated this tab away and lost the page. Reserved only when the customer chose to pay
    // now; "pay later" needs no window at all.
    const payNow =
      cancelPackageChoice === "redeem" && cancelPayTiming === "now";
    const reservedTab = payNow ? reservePaymentTab() : null;

    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        alert("Session expired. Please sign in again.");
        return;
      }

      // Group bookings always send the scope: a group is ONE Stripe payment, so the
      // server prices and refunds it once, and an anchor selection cancels the whole
      // group rather than orphaning the members whose times chain off the anchor.
      // A booking that is not in a group sends nothing and keeps the old behaviour.
      const cancelBookingIds = cancelBookingData.booking.groupBookingId
        ? cancelSelection
        : undefined;

      // 22G — the package choice. Only meaningful for a booking that spent package sessions; for every other
      // booking the server has nothing to settle and ignores it.
      const packageChoice = cancelBookingData.packageCancellation
        ? cancelPackageChoice
        : undefined;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${cancelBookingData.booking._id}/customer-cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason: "Customer cancelled via dashboard",
            ...(cancelBookingIds ? { cancelBookingIds } : {}),
            ...(packageChoice ? { packageCancellationChoice: packageChoice } : {}),
            // WHEN THEY INTEND TO PAY, so the server knows whether to DEFER the cancellation email: with "now"
            // there is a payment pending, and an email written before the card details are typed can only
            // describe a cancellation that is not yet settled (reported 2026-10-16).
            ...(packageChoice
              ? { packageCancellationPayTiming: cancelPayTiming }
              : {}),
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        // The package's own outcome is reported separately from the refund: a customer who LOST sessions and
        // chose to redeem them needs to be taken to the payment, not told "cancelled successfully" and left.
        const packageResult = data.packageCancellation || null;
        // THE RESPONSE FIRST, the pre-cancel warning only as a fallback. The settlement names the purchase on
        // EVERY outcome, and reading it from the pre-cancel payload alone is why "pay now" silently did nothing
        // when that payload did not carry one (reported 2026-10-16).
        const purchaseId =
          packageResult?.purchaseId ||
          cancelBookingData.packageCancellation?.purchaseId ||
          null;

        const msg = data.queued
          ? "Your cancellation request has been submitted for review. The shop will contact you within 24–48 hours."
          : packageResult?.outcome === "held_for_redemption"
            ? `Booking cancelled. ${packageResult.unitsLost || 0} package session(s) were lost — pay to get them back now, or from the booking page whenever you want to use them.`
            : // The refund could not be issued. Saying "cancelled successfully" here would tell a customer their
              // money is on its way when it is not, and it is the one case where they need to act.
              packageResult?.outcome === "refund_failed"
              ? `Booking cancelled. Your card refund of $${Number(
                  packageResult.refundAmount || 0,
                ).toFixed(2)} could not be issued automatically, and your sessions are held for you in the meantime. Please contact the shop so they can refund you — you can also get the sessions back on the booking page.`
              : packageResult?.outcome === "refunded"
                ? `Booking cancelled. $${Number(
                    packageResult.refundAmount || 0,
                  ).toFixed(2)} is being returned to your card.`
                : data.message || "Booking cancelled successfully";
        alert(msg);

        setCancelBookingData(null);
        setCancelTargets([]);
        setCancelSelection([]);
        setCancelPackageChoice("");
        setCancelPayTiming("now");
        setCancelPayScope("this");
        fetchAllBookings(); // Refresh list

        // OPEN THE PAYMENT LAST, so the confirmation above has been seen and the page state is already reset.
        // ONLY for "pay now": the customer who chose "later" gets the offer on their booking page, and a window
        // opened anyway would be a payment they did not ask to make yet.
        if (payNow && packageResult?.outcome === "held_for_redemption") {
          // "ALL" SENDS BOTH LISTS: the holds already on the package from earlier cancellations, and the ones
          // this cancellation just created. Pricing from the ids that were DISPLAYED is what keeps the charge
          // equal to the figure the dialog showed — the whole point of naming them.
          const scopeIds =
            cancelPayScope === "all"
              ? [
                  ...(cancelBookingData.packageCancellation?.otherHeldEntryIds || []),
                  ...(data.packageHoldEntryIds || []),
                ]
              : data.packageHoldEntryIds || null;
          await startPackageRedemption(
            purchaseId,
            reservedTab,
            data.packageRedemptionToken || null,
            scopeIds,
          );
        } else if (reservedTab) {
          // Nothing to pay: a reserved blank tab must never be left behind.
          try {
            reservedTab.close();
          } catch {
            // Nothing to do.
          }
        }
      } else {
        if (reservedTab) {
          try {
            reservedTab.close();
          } catch {
            // Nothing to do.
          }
        }
        alert(data.message || "Failed to cancel booking");
      }
    } catch (err) {
      if (reservedTab) {
        try {
          reservedTab.close();
        } catch {
          // Nothing to do.
        }
      }
      alert("Error cancelling booking");
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(profileForm),
        },
      );

      if (response.ok) {
        const result = await response.json();
        alert("Profile updated successfully");
        setIsEditingProfile(false);

        // Refresh dashboard data to show updated values
        await fetchDashboardData();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to update profile");
      }
    } catch (err) {
      alert("Error updating profile: " + err.message);
    }
  };

  const handleConfirmHistoryClaim = async () => {
    try {
      setHistoryClaimLoading(true);
      setHistoryClaimError("");
      const token = localStorage.getItem("ogestyle_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers/history-claim/confirm`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (!response.ok) {
        setHistoryClaimError(
          data?.message || "Unable to claim your history right now.",
        );
        return;
      }

      setShowHistoryClaimPrompt(false);
      await fetchDashboardData();
      if (activeTab === "bookings") await fetchAllBookings();
      if (activeTab === "orders") await fetchAllOrders();
    } catch (err) {
      setHistoryClaimError("Unable to claim your history right now.");
    } finally {
      setHistoryClaimLoading(false);
    }
  };

  const handleDismissHistoryClaim = async () => {
    try {
      setHistoryClaimLoading(true);
      setHistoryClaimError("");
      const token = localStorage.getItem("ogestyle_token");

      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers/history-claim/dismiss`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setShowHistoryClaimPrompt(false);
      setHistoryClaimPreview((prev) =>
        prev
          ? {
              ...prev,
              claimStatus: "dismissed",
              showPrompt: false,
            }
          : prev,
      );
    } catch (err) {
      setHistoryClaimError("Unable to update your preference right now.");
    } finally {
      setHistoryClaimLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBookingSupportContact = (booking) => {
    const staffRole = booking?.staffId?.role;
    const providerPhone = booking?.staffId?.userId?.phone || null;
    const shopPhone = booking?.shopId?.phone || null;
    const isIndependentProvider = ["R4", "R7"].includes(staffRole);

    if (isIndependentProvider && providerPhone) {
      return {
        label: "Provider",
        phone: providerPhone,
        reason: "R4/R7 booking",
      };
    }

    return {
      label: "Shop",
      phone: shopPhone || providerPhone,
      reason: "R1/shop booking",
    };
  };

  const getOrderSupportContact = (shop) => {
    const hasIndependentProducts = (shop?.items || []).some((item) =>
      ["R4", "R7"].includes(item?.sellerRole),
    );
    const independentBooking = (shop?.bookings || []).find(
      (booking) =>
        ["R4", "R7"].includes(booking?.staffRole) &&
        Boolean(booking?.professionalPhone),
    );

    if (hasIndependentProducts || independentBooking) {
      return {
        label: "Provider",
        phone: independentBooking?.professionalPhone || shop?.shopId?.phone,
      };
    }

    return {
      label: "Shop",
      phone: shop?.shopId?.phone,
    };
  };

  const getBookingProfessionalName = (booking) => {
    const staffUser = booking?.staffId?.userId;
    if (staffUser?.firstName || staffUser?.lastName) {
      return `${staffUser?.firstName || ""} ${staffUser?.lastName || ""}`.trim();
    }

    if (booking?.staffId?.firstName || booking?.staffId?.lastName) {
      return `${booking?.staffId?.firstName || ""} ${booking?.staffId?.lastName || ""}`.trim();
    }

    return booking?.professionalName || "Professional";
  };

  /**
   * 22G — THE PACKAGE SIDE OF THE SELECTION, TOTALLED FROM WHAT THE SERVER PRICED.
   *
   * Reported by the user, 2026-10-14: *"the information on the cancellation page … is not completely true about
   * the number of sessions that will be lost, the system should respond based on the number of actual bookings
   * that is to be cancelled most especially for group booking."* The dialog described the ONE booking that was
   * clicked, so ticking two said "1 session".
   *
   * EVERY FIGURE COMES FROM THE SERVER (`packageCancellations`, keyed by booking id); this only ADDS the ticked
   * ones. No tier is evaluated and no frozen price is read here — a second implementation of the policy is how a
   * screen comes to promise a number the charge does not honour.
   *
   * The two sides are kept apart on purpose, because they are different things: SESSIONS are lost from the
   * package, MONEY is refunded to the card (the card side comes from `cancelPreview`), and a group can be one of
   * each.
   */
  const cancelPackagePreview = useMemo(() => {
    const map = cancelBookingData?.packageCancellations || {};
    const ticked = cancelTargets.filter((target) =>
      cancelSelection.includes(target._id),
    );
    const rows = (ticked.length > 0
      ? ticked
      : cancelBookingData?.booking
        ? [cancelBookingData.booking]
        : []
    )
      .map((booking) => map[String(booking._id)])
      .filter(Boolean);

    if (rows.length === 0) return null;

    const currencies = [...new Set(rows.map((row) => row.currency))];
    const sum = (field) =>
      Math.round(rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0) * 100) / 100;

    return {
      bookings: rows.length,
      units: rows.reduce((acc, row) => acc + (Number(row.units) || 0), 0),
      // "in time" only when EVERY ticked booking is: one late cancellation in the selection is what decides that
      // something is lost, and saying "in time" on a mixed selection would hide it.
      inTime: rows.every((row) => row.inTime),
      // The band, when every ticked booking shares it — the same rule the server's totals use. Used only for the
      // "late-cancellation share" breakdown; the amounts are always the server's.
      feePercent: rows.every((row) => row.feePercent === rows[0].feePercent)
        ? rows[0].feePercent
        : null,
      // THE VALUE OF THE SESSIONS, so the fee line can show the arithmetic instead of "Value $0.00".
      basis: sum("basis"),
      redeemAmount: sum("redeemAmount"),
      // THE SINGLE RULE (user, 2026-10-17): *"The only time the option for refund to the card is not available is
      // when there's $0 to the customer/guest."* There is no `refundEligible` flag any more — the refund is
      // offered exactly when this number is above zero, and the fee is carried so the deduction can be shown.
      refundAmount: sum("refundAmount"),
      processingFee: sum("processingFee"),
      netBasis: sum("netBasis"),
      currency: currencies.length === 1 ? currencies[0] : null,
      purchaseId: rows[0].purchaseId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelBookingData, cancelTargets, cancelSelection]);

  const getBookingShopName = (booking) => {
    const shop = booking?.shopId;

    if (shop && typeof shop === "object" && shop.name) {
      return shop.name;
    }

    return booking?.shopName || booking?.businessName || "Shop information unavailable";
  };

  const getBookingDateTime = (booking) => {
    if (booking?.appointmentDateTimeUTC) {
      return new Date(booking.appointmentDateTimeUTC);
    }

    if (booking?.appointmentDate && booking?.startTime) {
      return new Date(`${booking.appointmentDate}T${booking.startTime}`);
    }

    return null;
  };

  const getBookingServicePrice = (booking) =>
    booking?.servicePriceAtBooking ||
    booking?.serviceId?.price ||
    booking?.servicePrice ||
    booking?.amount ||
    0;

  // Per-booking preview for whatever is currently ticked, priced with the SAME bands
  // the server will charge (see utils/cancellationPolicyMirror.js).
  const cancelRefundRows = useMemo(() => {
    const ticked = cancelTargets.filter((target) =>
      cancelSelection.includes(target._id),
    );
    const rows =
      ticked.length > 0
        ? ticked
        : cancelBookingData?.booking
          ? [cancelBookingData.booking]
          : [];

    return rows.map((booking) => {
      const bookingDateTime = getBookingDateTime(booking);
      const hoursUntilAppointment = bookingDateTime
        ? (bookingDateTime - new Date()) / (1000 * 60 * 60)
        : 0;
      // null = not knowable yet. Never render that as "0% fee".
      const feePercent = cancellationPolicyReady
        ? resolveCancellationFeePercent(
            hoursUntilAppointment,
            cancellationPolicy,
          )
        : null;
      // ── A PACKAGE BOOKING IS WORTH WHAT WAS PAID FOR ITS SESSIONS, NOT THE LIST PRICE (user, 2026-10-16) ──
      //
      // The user: *"the cancellation page is not using the discounted price for the group member, it uses the
      // right amount for the primary member"*. These rows read `servicePriceAtBooking` — the shop's LIST price —
      // so an $80 package showed $30 and $20 for two sessions whose package value is $24 and $16. The server
      // already prices each booking's own loss in `packageCancellations` (its `basis` is the PAID value of the
      // units that booking spent), so a package row takes its figure from there and says so.
      const packageRow = cancelBookingData?.packageCancellations?.[String(booking._id)] || null;
      const servicePrice = packageRow
        ? Number(packageRow.basis) || 0
        : getBookingServicePrice(booking);

      return {
        bookingId: booking._id,
        label: booking.isPrimaryGroupBooking
          ? "Your booking"
          : booking.groupMemberName || "Group member",
        hoursUntilAppointment: Math.round(hoursUntilAppointment),
        feePercent,
        servicePrice,
        // Reads the price as a package value, and stops a CARD fee being implied on a booking the card never paid.
        isPackageValue: Boolean(packageRow),
        cancellationFee:
          feePercent === null || packageRow ? null : servicePrice * (feePercent / 100),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cancelTargets,
    cancelSelection,
    cancelBookingData,
    cancellationPolicy,
    cancellationPolicyReady,
  ]);

  // Aggregated for the modal: a single ticked booking behaves exactly as it did before
  // the group scope existed, because the sums collapse to that one booking.
  const cancelPreview = useMemo(() => {
    if (cancelRefundRows.length === 0) return null;

    const servicePrice = cancelRefundRows.reduce(
      (sum, row) => sum + row.servicePrice,
      0,
    );
    // A PACKAGE ROW CARRIES NO CARD FEE (2026-10-17). Its figure is the package's value and its money is settled
    // by the package block above; adding a card cancellation fee for it would state a second, invented charge.
    const feeKnown = cancelRefundRows.every(
      (row) => row.isPackageValue || row.feePercent !== null,
    );
    const cancellationFee = feeKnown
      ? cancelRefundRows.reduce((sum, row) => sum + (row.cancellationFee || 0), 0)
      : null;

    // A group is one appointment, so the bands normally agree. If they somehow do not
    // (members straddling a boundary), report the amount without claiming a single
    // percentage for it.
    const percents = cancelRefundRows.map((row) => row.feePercent);
    const uniformFeePercent =
      percents.every((value) => value === percents[0]) ? percents[0] : null;

    return {
      servicePrice,
      cancellationFee,
      feePercent: uniformFeePercent,
      feeKnown,
      hoursUntilAppointment: cancelRefundRows[0].hoursUntilAppointment,
      refundAmount:
        cancellationFee === null ? null : servicePrice - cancellationFee,
      isGroup: cancelTargets.length > 1,
      policyBullets: describeCancellationPolicy(cancellationPolicy),
    };
  }, [cancelRefundRows, cancelTargets.length, cancellationPolicy]);

  const getBookingAddress = (booking) =>
    [
      booking?.shopId?.street,
      booking?.shopId?.city,
      booking?.shopId?.state,
      booking?.shopId?.zipCode,
    ]
      .filter(Boolean)
      .join(", ");

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="customer-dashboard-page">
          <div className="loading">Loading dashboard...</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error) {
    return (
      <>
        <CustomerHeader />
        <div className="customer-dashboard-page">
          <div className="error-container">
            <p>⚠️ {error}</p>
            <button onClick={() => navigate("/")}>Return Home</button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const { customer, upcomingBookings, recentOrders, activePackages } =
    dashboardData || {};

  return (
    <>
      <CustomerHeader />
      <div className="customer-dashboard-page">
        {showHistoryClaimPrompt && historyClaimPreview?.claimable?.total > 0 && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "16px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "540px",
                background: "#ffffff",
                borderRadius: "14px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 12px 30px rgba(2, 6, 23, 0.18)",
                padding: isMobile ? "18px" : "24px",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: "8px", color: "#0f172a" }}>
                We found past activity for your email
              </h3>
              <p style={{ marginTop: 0, color: "#334155", lineHeight: 1.55 }}>
                Before we add it to your account, please confirm below.
              </p>

              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "12px",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                <div>
                  Bookings found: <strong>{historyClaimPreview.claimable.bookings}</strong>
                </div>
                <div>
                  Orders found: <strong>{historyClaimPreview.claimable.orders}</strong>
                </div>
                <div>
                  Total records: <strong>{historyClaimPreview.claimable.total}</strong>
                </div>
              </div>

              <p style={{ marginTop: 0, color: "#64748b", fontSize: "13px" }}>
                Choosing "Add to My Account" links only records with your exact email and no existing account owner.
              </p>

              {historyClaimError && (
                <div
                  style={{
                    marginBottom: "10px",
                    color: "#b91c1c",
                    fontSize: "13px",
                  }}
                >
                  {historyClaimError}
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={handleDismissHistoryClaim}
                  disabled={historyClaimLoading}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    color: "#334155",
                    cursor: historyClaimLoading ? "not-allowed" : "pointer",
                  }}
                >
                  Not Now
                </button>
                <button
                  type="button"
                  onClick={handleConfirmHistoryClaim}
                  disabled={historyClaimLoading}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "none",
                    background: historyClaimLoading ? "#94a3b8" : "#1a659e",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: historyClaimLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {historyClaimLoading ? "Adding..." : "Add to My Account"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="dashboard-container">
          {/* Header */}
          <div className="dashboard-header">
            <div className="welcome-section">
              <h1>Welcome back, {customer?.firstName}!</h1>
              <p className="member-since">
                Member since{" "}
                {new Date(customer?.createdAt).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
            <div className="quick-stats">
              <div className="stat-card">
                <span className="stat-number">
                  {customer?.totalBookings || 0}
                </span>
                <span className="stat-label">Total Bookings</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">
                  {formatCurrency(customer?.totalSpent || 0)}
                </span>
                <span className="stat-label">Total Spent</span>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="dashboard-tabs">
            <button
              className={activeTab === "overview" ? "tab active" : "tab"}
              onClick={() => setActiveTab("overview")}
            >
              📊 Overview
            </button>
            <button
              className={activeTab === "bookings" ? "tab active" : "tab"}
              onClick={() => setActiveTab("bookings")}
            >
              📅 Bookings
            </button>
            <button
              className={activeTab === "orders" ? "tab active" : "tab"}
              onClick={() => setActiveTab("orders")}
            >
              📦 Orders
            </button>
            <button
              className={activeTab === "packages" ? "tab active" : "tab"}
              onClick={() => setActiveTab("packages")}
            >
              💎 Packages
            </button>
            <button
              className={activeTab === "profile" ? "tab active" : "tab"}
              onClick={() => setActiveTab("profile")}
            >
              ⚙️ Profile
            </button>
          </div>

          {/* Content Area */}
          <div className="dashboard-content">
            {activeTab === "overview" && (
              <div className="overview-tab">
                {historyClaimPreview &&
                  (historyClaimPreview?.claimable?.total || 0) > 0 && (
                    <section className="dashboard-section">
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          padding: "14px",
                          background: "#f8fafc",
                        }}
                      >
                        <h2 style={{ marginTop: 0, marginBottom: "8px" }}>
                          Claim Previous Activity
                        </h2>
                        <p
                          style={{
                            marginTop: 0,
                            color: "#475569",
                            fontSize: "14px",
                          }}
                        >
                          We found history under your email that is not yet linked to this account.
                        </p>
                        <p
                          style={{
                            margin: "0 0 10px",
                            color: "#0f172a",
                            fontSize: "13px",
                          }}
                        >
                          Bookings: {historyClaimPreview.claimable.bookings} | Orders: {historyClaimPreview.claimable.orders}
                        </p>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={handleConfirmHistoryClaim}
                            disabled={historyClaimLoading}
                            style={{
                              padding: "9px 12px",
                              borderRadius: "8px",
                              border: "none",
                              background: historyClaimLoading
                                ? "#94a3b8"
                                : "#1a659e",
                              color: "#fff",
                              fontWeight: 600,
                              cursor: historyClaimLoading
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            {historyClaimLoading ? "Adding..." : "Claim History"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("profile")}
                            style={{
                              padding: "9px 12px",
                              borderRadius: "8px",
                              border: "1px solid #cbd5e1",
                              background: "#fff",
                              color: "#334155",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Review in Profile
                          </button>
                        </div>
                        {historyClaimError && (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#b91c1c",
                            }}
                          >
                            {historyClaimError}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                {/* Upcoming Bookings */}
                <section className="dashboard-section">
                  <h2>Upcoming Appointments</h2>
                  {upcomingBookings && upcomingBookings.length > 0 ? (
                    <div className="bookings-list">
                      {upcomingBookings.slice(0, 3).map((booking) => (
                        <div key={booking._id} className="booking-card">
                          <div className="booking-info">
                            <h3>{booking.serviceName}</h3>
                            <p className="datetime">
                              📅{" "}
                              {booking.appointmentDate && booking.startTime
                                ? new Intl.DateTimeFormat("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    timeZone:
                                      booking.shopTimezone || "America/Chicago",
                                  }).format(
                                    new Date(
                                      `${booking.appointmentDate}T${booking.startTime}:00`,
                                    ),
                                  )
                                : "Date pending"}
                            </p>
                          </div>
                          <div className="booking-actions">
                            <button
                              className="btn-small"
                              onClick={() => {
                                // Fetch full booking details before opening modal
                                fetch(
                                  `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${booking._id}`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
                                    },
                                  },
                                )
                                  .then((res) => res.json())
                                  .then((data) => {
                                    setSelectedBooking(data.booking);
                                  })
                                  .catch((err) => {
                                    alert("Failed to load booking details");
                                  });
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No upcoming appointments</p>
                      <button onClick={() => navigate("/shops")}>
                        Book Now
                      </button>
                    </div>
                  )}
                </section>

                {/* Active Packages */}
                {activePackages && activePackages.length > 0 && (
                  <section className="dashboard-section">
                    <h2>Active Packages</h2>
                    <div className="packages-grid">
                      {activePackages.map((pkg) => (
                        <div key={pkg._id} className="package-card">
                          <h3>{pkg.packageName}</h3>
                          <div className="package-credits">
                            <span className="credits-remaining">
                              {pkg.sessionsRemaining}
                            </span>
                            <span className="credits-total">
                              of {pkg.totalSessions} remaining
                            </span>
                          </div>
                          <p className="expires">
                            Expires{" "}
                            {new Date(pkg.expiryDate).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Recent Orders */}
                <section className="dashboard-section">
                  <h2>Recent Orders</h2>
                  {recentOrders && recentOrders.length > 0 ? (
                    <div className="orders-list">
                      {recentOrders.slice(0, 3).map((order) => (
                        <div key={order._id} className="order-card">
                          <div className="order-info">
                            <strong>{order.orderNumber}</strong>
                            <span className="order-date">
                              {order.createdAt
                                ? new Date(order.createdAt).toLocaleString(
                                    "en-US",
                                    {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                    },
                                  )
                                : "N/A"}
                            </span>
                          </div>
                          <div className="order-status">
                            <span
                              className={`status-badge status-${order.status}`}
                            >
                              {order.status}
                            </span>
                          </div>
                          <div className="order-total">
                            {formatCurrency(order.totalAmount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No orders yet</p>
                      <button onClick={() => navigate("/shops")}>
                        Start Shopping
                      </button>
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeTab === "bookings" && (
              <div className="bookings-tab">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <h2 style={{ margin: 0 }}>All Bookings</h2>
                  <button
                    onClick={() => navigate("/shops")}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#2d6da3",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    📅 Book Another Appointment
                  </button>
                </div>

                {/* Filter buttons */}
                <div className="filter-buttons">
                  <button
                    className={bookingFilter === "all" ? "active" : ""}
                    onClick={() => setBookingFilter("all")}
                  >
                    All
                  </button>
                  <button
                    className={bookingFilter === "confirmed" ? "active" : ""}
                    onClick={() => setBookingFilter("confirmed")}
                  >
                    Upcoming
                  </button>
                  <button
                    className={bookingFilter === "completed" ? "active" : ""}
                    onClick={() => setBookingFilter("completed")}
                  >
                    Past
                  </button>
                  <button
                    className={bookingFilter === "cancelled" ? "active" : ""}
                    onClick={() => setBookingFilter("cancelled")}
                  >
                    Cancelled
                  </button>
                </div>

                {/* Bookings list */}
                {bookings && bookings.length > 0 ? (
                  <div className="bookings-list">
                    {bookings
                      .filter(
                        (booking) =>
                          bookingFilter === "all" ||
                          booking.status === bookingFilter,
                      )
                      .map((booking) => (
                        <div key={booking._id} className="booking-card">
                          <div className="booking-header">
                            <h3>{booking.serviceId?.name || "Service"}</h3>
                            <span
                              className={`status-badge status-${booking.status}`}
                            >
                              {booking.status === "slot_conflict"
                                ? "time needs confirming"
                                : booking.status}
                            </span>
                          </div>
                          {/* Part B: the time was taken mid-payment. Shown here as
                              well as on the return page so it cannot be missed. */}
                          <SlotConflictPanel
                            booking={booking}
                            variant="compact"
                            keeping={keepingSlotConflictId === booking._id}
                            keepError={
                              slotConflictErrorBookingId === booking._id
                                ? slotConflictError
                                : ""
                            }
                            onKeep={handleKeepSlotConflictTime}
                          />
                          <div className="booking-details">
                            <p>
                              <strong>Professional:</strong>{" "}
                              {booking.staffId?.userId?.firstName &&
                              booking.staffId?.userId?.lastName
                                ? `${booking.staffId.userId.firstName} ${booking.staffId.userId.lastName}`
                                : "N/A"}
                            </p>
                            <p>
                              <strong>Shop:</strong>{" "}
                              {booking.shopId?.name || "N/A"}
                            </p>
                            <p>
                              <strong>Date:</strong>{" "}
                              {booking.appointmentDate && booking.startTime
                                ? new Intl.DateTimeFormat("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    timeZone:
                                      booking.shopTimezone || "America/Chicago",
                                  }).format(
                                    new Date(
                                      `${booking.appointmentDate}T${booking.startTime}:00`,
                                    ),
                                  )
                                : "Date not available"}
                            </p>
                            <p>
                              <strong>Duration:</strong>{" "}
                              {booking.serviceId?.durationMinutes ||
                                booking.serviceId?.duration ||
                                booking.duration ||
                                "N/A"}{" "}
                              min
                            </p>
                          </div>
                          <div className="booking-actions">
                            <button
                              className="btn-secondary"
                              onClick={() => {
                                // Fetch full booking details before opening modal
                                fetch(
                                  `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${booking._id}`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
                                    },
                                  },
                                )
                                  .then((res) => res.json())
                                  .then((data) => {
                                    setSelectedBooking(data.booking);
                                  })
                                  .catch((err) => {
                                    alert("Failed to load booking details");
                                  });
                              }}
                            >
                              View Details
                            </button>
                            {booking.status === "confirmed" && (
                              <button
                                className="btn-danger"
                                onClick={() => handleCancelBooking(booking)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No bookings found</p>
                    <button onClick={() => navigate("/freelancers")}>
                      Book a Service
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "orders" && (
              <div className="orders-tab">
                <h2>Order History</h2>

                {orders && orders.length > 0 ? (
                  <div className="orders-full-list">
                    {orders.map((order) => (
                      <div key={order._id} className="order-card-full">
                        <div className="order-header-full">
                          <div>
                            <h3>{order.orderNumber}</h3>
                            <p className="order-date-full">
                              {new Date(order.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </p>
                          </div>
                          <div className="order-total-full">
                            <p className="total-label">Total</p>
                            <p className="total-amount">
                              {formatCurrency(order.totalAmount)}
                            </p>
                          </div>
                        </div>

                        <div className="order-status-full">
                          <span
                            className={`status-badge status-${order.status}`}
                          >
                            {order.status}
                          </span>
                        </div>

                        <div className="order-actions-full">
                          <button
                            className="btn-secondary"
                            onClick={async () => {
                              if (expandedOrderId === order._id) {
                                setExpandedOrderId(null);
                                setExpandedOrderData(null);
                              } else {
                                try {
                                  const response = await fetch(
                                    `${process.env.REACT_APP_API_BASE_URL}/api/orders/track?orderNumber=${order.orderNumber}&email=${dashboardData?.customer?.email}`,
                                  );
                                  if (response.ok) {
                                    const data = await response.json();
                                    setExpandedOrderData(data.order);
                                    setExpandedOrderId(order._id);
                                  }
                                } catch (err) {
                                }
                              }
                            }}
                          >
                            {expandedOrderId === order._id
                              ? "Hide Details"
                              : "View Order"}
                          </button>
                        </div>

                        {expandedOrderId === order._id && expandedOrderData && (
                          <div className="order-details-expanded">
                            {expandedOrderData.shops &&
                              expandedOrderData.shops.map((shop, idx) => (
                                <div
                                  key={idx}
                                  className="shop-section-expanded"
                                >
                                  <h4>{shop.shopName}</h4>
                                  {(() => {
                                    const supportContact =
                                      getOrderSupportContact(shop);
                                    return supportContact.phone ? (
                                      <p
                                        style={{
                                          fontSize: "12px",
                                          marginBottom: "10px",
                                        }}
                                      >
                                        For product + service refund/return
                                        support, call{" "}
                                        {supportContact.label.toLowerCase()}:{" "}
                                        <a href={`tel:${supportContact.phone}`}>
                                          {supportContact.phone}
                                        </a>
                                      </p>
                                    ) : (
                                      <p
                                        style={{
                                          fontSize: "12px",
                                          marginBottom: "10px",
                                        }}
                                      >
                                        For product + service refund/return
                                        support, call the shop directly.
                                      </p>
                                    );
                                  })()}

                                  {/* Products (direct purchases) */}
                                  {shop.items && shop.items.length > 0 && (
                                    <div className="items-section">
                                      <h5>Products</h5>
                                      {shop.items.map((item, itemIdx) => (
                                        <div
                                          key={itemIdx}
                                          className="order-item"
                                        >
                                          {item.image && (
                                            <img
                                              src={item.image}
                                              alt={item.name}
                                              className="product-thumbnail"
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          )}
                                          <div className="item-info">
                                            <span className="item-name">
                                              {item.name}
                                            </span>
                                            <span className="item-quantity">
                                              Qty: {item.quantity}
                                            </span>
                                          </div>
                                          <span className="item-price">
                                            {formatCurrency(
                                              item.priceAtPurchase *
                                                item.quantity,
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Products from Bookings */}
                                  {shop.bookings &&
                                    shop.bookings.some(
                                      (b) =>
                                        b.products && b.products.length > 0,
                                    ) && (
                                      <div className="items-section">
                                        <h5>Products</h5>
                                        {shop.bookings.map(
                                          (booking, bookingIdx) =>
                                            booking.products &&
                                            booking.products.map(
                                              (product, productIdx) => (
                                                <div
                                                  key={`${bookingIdx}-${productIdx}`}
                                                  className="order-item"
                                                >
                                                  {product.productImage && (
                                                    <img
                                                      src={product.productImage}
                                                      alt={product.productName}
                                                      className="product-thumbnail"
                                                      onError={(e) => {
                                                        e.target.style.display =
                                                          "none";
                                                      }}
                                                    />
                                                  )}
                                                  <div className="item-info">
                                                    <span className="item-name">
                                                      {product.productName}
                                                    </span>
                                                    <span className="item-quantity">
                                                      Qty: {product.quantity}
                                                    </span>
                                                    <span className="pickup-badge">
                                                      Pickup at appointment
                                                    </span>
                                                  </div>
                                                  <span className="item-price">
                                                    {formatCurrency(
                                                      product.priceAtBooking *
                                                        product.quantity,
                                                    )}
                                                  </span>
                                                </div>
                                              ),
                                            ),
                                        )}
                                      </div>
                                    )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No orders yet</p>
                    <button onClick={() => navigate("/shops")}>
                      Start Shopping
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "packages" && (
              <div className="packages-tab">
                <h2>My Packages</h2>

                {packages && packages.length > 0 ? (
                  <div className="packages-full-list">
                    {packages.map((purchase) => (
                      <div key={purchase._id} className="package-card-full">
                        {/* THE CARD FIRST, before the credits and the progress bar. The number is what the
                            holder needs in order to USE the package or give it away, and it is the thing
                            they would otherwise have to go back to the purchase email to find. */}
                        <PackageCard
                          accountNumber={purchase.accountNumber}
                          holderName={
                            // THE PURCHASE'S OWN FIELDS. There is no `customerName` on the model — it is
                            // `customerFirstName` (+ optional `customerLastName`), which is why the card was
                            // falling back to the email address.
                            [
                              purchase.customerFirstName,
                              purchase.customerLastName,
                            ]
                              .filter(Boolean)
                              .join(" ") ||
                            customer?.firstName ||
                            ""
                          }
                          validUntil={purchase.validUntil}
                          totalSessions={purchase.totalSessions}
                          shopName={purchase.shopId?.name}
                          packageName={purchase.packageId?.name}
                          status={purchase.status}
                        />

                        {/* GIVING IT AWAY (user, 2026-10-09). Offered ONLY when the server says it is
                            possible: the flag comes from `mayTransfer`, the same rules the transfer enforces,
                            so the button cannot appear on a package that would then be refused — which is how a
                            screen and a rule drift apart. The REASON is shown instead when there is one, so a
                            customer is told why rather than left wondering where the button went.

                            A guest reaches the same page from the link in the purchase email; that is why the
                            page is public and this entry point is not the only one. */}
                        {purchase.transfer?.ok ? (
                          <a
                            href={`/packages/${purchase._id}/transfer`}
                            style={{
                              display: "inline-block",
                              marginBottom: "14px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#4a90c6",
                              textDecoration: "none",
                            }}
                          >
                            Giving this package to someone? Start the transfer &rarr;
                          </a>
                        ) : purchase.transfer?.message ? (
                          <p
                            style={{
                              margin: "0 0 14px 0",
                              fontSize: "13px",
                              color: "#6b7280",
                            }}
                          >
                            {purchase.transfer.message}
                          </p>
                        ) : null}
                        <div className="package-header-full">
                          <h3>{purchase.packageId?.name || "Package"}</h3>
                          {/* THE BADGE FOLLOWS THE DATE (reported 2026-10-19: an expired card still carried a
                              green ACTIVE pill). `status` in the database stays `active` until the nightly sweep
                              decides the package is finally abandoned, so between the deadline and that run the
                              stored status contradicts the card above it. The date is the fact. */}
                          <span
                            className={`status-badge status-${
                              isPurchaseExpired(purchase)
                                ? "expired"
                                : purchase.status
                            }`}
                            style={
                              isPurchaseExpired(purchase)
                                ? {
                                    background: "#fee2e2",
                                    color: "#991b1b",
                                    fontWeight: 700,
                                  }
                                : undefined
                            }
                          >
                            {isPurchaseExpired(purchase)
                              ? "expired"
                              : purchase.status}
                          </span>
                        </div>

                        <div className="package-details-full">
                          <p>
                            {/* SESSIONS, NOT "CREDITS" (reported 2026-10-19: "Credits Remaining: /" and
                                "NaN% remaining"). This read `creditsRemaining` / `totalCredits`, which a
                                PackagePurchase does not have — it has `sessionsRemaining` / `totalSessions` — so
                                both read `undefined` and the percentage was NaN. */}
                            <strong>Sessions remaining:</strong>{" "}
                            {/* THE REAL NUMBER, IN RED WHEN THEY CANNOT BE USED (user, 2026-10-19: *"we should
                                show the actual number of sessions remaining even for an expired package, the only
                                thing is that it could be displayed in red and cannot be used"*). Showing 0 / 4 said
                                the sessions had been spent; they have not — they are waiting behind a reactivation. */}
                            <span
                              style={
                                isPurchaseExpired(purchase)
                                  ? { color: "#b91c1c", fontWeight: 700 }
                                  : undefined
                              }
                            >
                              {Number(
                                purchase.sessionsRemaining ??
                                  (Number(purchase.totalSessions) || 0) -
                                    (Number(purchase.sessionsUsed) || 0),
                              ) || 0}{" "}
                              / {Number(purchase.totalSessions) || 0}
                            </span>
                          </p>
                          <p>
                            <strong>Purchased:</strong>{" "}
                            {new Date(
                              purchase.purchaseDate,
                            ).toLocaleDateString()}
                          </p>
                          {purchase.expiryDate && (
                            <p>
                              <strong>Expires:</strong>{" "}
                              {new Date(
                                purchase.expiryDate,
                              ).toLocaleDateString()}
                            </p>
                          )}
                          <p>
                            <strong>Shop:</strong> {purchase.shopId?.name}
                          </p>
                        </div>

                        {purchase.packageId?.description && (
                          <p className="package-description">
                            {purchase.packageId.description}
                          </p>
                        )}

                        <div className="package-progress">
                          <div className="progress-bar">
                            {(() => {
                              // ONE division, guarded. Two `undefined / undefined` reads is how this printed
                              // NaN% beside an empty "x / y".
                              const total = Number(purchase.totalSessions) || 0;
                              const left =
                                Number(
                                  purchase.sessionsRemaining ??
                                    total - (Number(purchase.sessionsUsed) || 0),
                                ) || 0;
                              const percent = total > 0 ? Math.round((left / total) * 100) : 0;
                              return (
                                <div
                                  className="progress-fill"
                                  style={{
                                    width: `${percent}%`,
                                    background: isPurchaseExpired(purchase)
                                      ? "#b91c1c"
                                      : undefined,
                                  }}
                                />
                              );
                            })()}
                          </div>
                          <p className="progress-text">
                            {(() => {
                              const total = Number(purchase.totalSessions) || 0;
                              const left =
                                Number(
                                  purchase.sessionsRemaining ??
                                    total - (Number(purchase.sessionsUsed) || 0),
                                ) || 0;
                              return (
                                <span
                                  style={
                                    isPurchaseExpired(purchase)
                                      ? { color: "#b91c1c", fontWeight: 700 }
                                      : undefined
                                  }
                                >
                                  {total > 0 ? Math.round((left / total) * 100) : 0}
                                </span>
                              );
                            })()}
                            % remaining
                            {isPurchaseExpired(purchase) ? " — unusable until reactivated" : ""}
                          </p>
                        </div>

                        {/* ── PHASE 10 — EXPIRY, AND THE RIGHT TO REVIVE IT (D24 / D27 / D28) ───────────────
                            Reactivation is a RIGHT: no approval, no queue. The figures are the server's own
                            (`renewal`), so the button charges what the server quoted. Shown only when the server
                            says it is possible — a package inside its window, or one that has used both
                            reactivations, renders nothing here. */}
                        {purchase.renewal?.state ===
                          "expired_awaiting_reactivation" &&
                          purchase.renewal?.canReactivate && (
                            <div
                              style={{
                                marginTop: "12px",
                                background: "#fffbeb",
                                border: "1px solid #fcd34d",
                                borderRadius: "8px",
                                padding: "12px 14px",
                                fontSize: "13px",
                                color: "#92400e",
                                lineHeight: 1.5,
                              }}
                            >
                              <strong>This package has expired.</strong> You can
                              reactivate it for{" "}
                              {purchase.renewal.windowDays} more day
                              {purchase.renewal.windowDays === 1 ? "" : "s"} for{" "}
                              {formatCurrency(purchase.renewal.fee)} — all{" "}
                              {purchase.renewal.undeliveredSessions} remaining session
                              {purchase.renewal.undeliveredSessions === 1 ? "" : "s"}{" "}
                              come back with it.
                              <div style={{ marginTop: "10px" }}>
                                <button
                                  type="button"
                                  onClick={() => reactivatePackage(purchase)}
                                  disabled={reactivatingId === purchase._id}
                                  style={{
                                    padding: "8px 14px",
                                    background: reactivatingId === purchase._id ? "#9ca3af" : "#92400e",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor:
                                      reactivatingId === purchase._id
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  {reactivatingId === purchase._id
                                    ? "Reactivating…"
                                    : purchase.renewal.fee > 0
                                      ? `Reactivate for ${formatCurrency(purchase.renewal.fee)}`
                                      : "Reactivate free"}
                                </button>
                              </div>
                            </div>
                          )}

                        <div className="package-actions-full">
                          {/* THE SHOP THAT SOLD IT, NOT A DIRECTORY (reported 2026-10-19: "book with package
                              button is opening /freelancers, this is absolutely wrong … shouldn't the package
                              open the shop's top page that sold the package"). `/freelancers` lists people who
                              have nothing to do with this package. The shop's own page is where its services and
                              its professionals are, so that is where the booking starts. Fallback to the shop
                              directory only when the purchase carries no slugs at all. */}
                          <button
                            className="btn-primary"
                            onClick={() => {
                              const businessSlug =
                                purchase.shopId?.businessId?.slug;
                              // AN EXPIRED PACKAGE CANNOT PAY FOR A BOOKING, so this is not "book with package" —
                              // it is "reactivate, then book" (user, 2026-10-19).
                              if (isPurchaseExpired(purchase)) {
                                reactivatePackage(purchase, { thenBook: true });
                                return;
                              }
                              // PLAN ITEM 2.12 (2026-10-23). This used to open `/business/<slug>` — the shop's
                              // top page — so a customer who had paid still had to pick a professional, find the
                              // service and re-select the package before it could be used. It now goes straight
                              // to `/booking` with THIS purchase and the first covered service preselected. The
                              // link spends nothing: the session is taken when the booking is confirmed.
                              const link = buildPackageBookingLink(purchase);
                              if (link.ok) {
                                navigate(link.href);
                                return;
                              }
                              // When it cannot be built, the reason is the message — and the shop page is
                              // still offered, because browsing is better than a dead button.
                              if (businessSlug) {
                                navigate(`/business/${businessSlug}`);
                                return;
                              }
                              navigate("/shops");
                            }}
                          >
                            {isPurchaseExpired(purchase)
                              ? "Reactivate and book with package"
                              : "Book with Package"}
                          </button>
                          {/* WHY THERE IS NO LINK, when there is none: the sentence the builder returned. */}
                          {!isPurchaseExpired(purchase) &&
                            (() => {
                              const link = buildPackageBookingLink(purchase);
                              return link.ok ? null : (
                                <p
                                  style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                  }}
                                >
                                  {link.reason} Use the shop page above, or ask the
                                  shop to help.
                                </p>
                              );
                            })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No active packages</p>
                    <button onClick={() => navigate("/packages")}>
                      Browse Packages
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "profile" && (
              <div className="profile-tab">
                <div className="profile-header">
                  <h2>Profile Settings</h2>
                  {!isEditingProfile ? (
                    <button
                      className="btn-secondary"
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button
                        className="btn-primary"
                        onClick={handleSaveProfile}
                      >
                        Save
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setIsEditingProfile(false);
                          setProfileForm({
                            firstName: customer?.firstName || "",
                            lastName: customer?.lastName || "",
                            email: customer?.email || "",
                            phone: customer?.phone || "",
                            billingAddress: customer?.billingAddress || {
                              street: "",
                              city: "",
                              state: "",
                              zip: "",
                              country: "",
                            },
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {customer && (
                  <div className="profile-form">
                    <h3 style={{ fontSize: "12px", marginBottom: "1rem" }}>
                      Personal Information
                    </h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={profileForm.firstName}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              firstName: e.target.value,
                            })
                          }
                          disabled={!isEditingProfile}
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={profileForm.lastName}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              lastName: e.target.value,
                            })
                          }
                          disabled={!isEditingProfile}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={profileForm.email}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              email: e.target.value,
                            })
                          }
                          disabled={true}
                          title="Email address cannot be changed"
                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          value={profileForm.phone}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              phone: e.target.value,
                            })
                          }
                          disabled={!isEditingProfile}
                          placeholder="(555) 555-5555"
                        />
                      </div>
                    </div>

                    <h3 style={{ fontSize: "12px", margin: "1.5rem 0 1rem" }}>
                      Billing Address
                    </h3>
                    <div className="form-group">
                      <label>Street Address</label>
                      {/* THE SHARED ADDRESS FIELD (2026-10-23). A customer typing a billing address by hand gets
                          whatever they typed; picking it from the list gives the street, city, state and ZIP that
                          Google resolved, which is what every later screen and email reads back to them. Shown
                          only while editing because the component has no read-only mode, and a disabled field
                          that still opens a suggestions list would be a worse lie than a plain input. */}
                      {isEditingProfile ? (
                        <AddressAutocompleteInput
                          value={profileForm.billingAddress.street}
                          onChange={(street) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              billingAddress: { ...prev.billingAddress, street },
                            }))
                          }
                          onAddressSelect={(address) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              billingAddress: {
                                ...prev.billingAddress,
                                street: address.street || prev.billingAddress.street,
                                city: address.city || prev.billingAddress.city,
                                state: address.state || prev.billingAddress.state,
                                zip: address.zip || prev.billingAddress.zip,
                                country:
                                  address.country || prev.billingAddress.country,
                              },
                            }))
                          }
                          placeholder="123 Main St"
                        />
                      ) : (
                        <input
                          type="text"
                          value={profileForm.billingAddress.street}
                          disabled
                          placeholder="123 Main St"
                        />
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          value={profileForm.billingAddress.city}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              billingAddress: {
                                ...profileForm.billingAddress,
                                city: e.target.value,
                              },
                            })
                          }
                          disabled={!isEditingProfile}
                        />
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          value={profileForm.billingAddress.state}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              billingAddress: {
                                ...profileForm.billingAddress,
                                state: e.target.value,
                              },
                            })
                          }
                          disabled={!isEditingProfile}
                          placeholder="TX"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>ZIP Code</label>
                        <input
                          type="text"
                          value={profileForm.billingAddress.zip}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              billingAddress: {
                                ...profileForm.billingAddress,
                                zip: e.target.value,
                              },
                            })
                          }
                          disabled={!isEditingProfile}
                          placeholder="75001"
                        />
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          value={profileForm.billingAddress.country}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              billingAddress: {
                                ...profileForm.billingAddress,
                                country: e.target.value,
                              },
                            })
                          }
                          disabled={!isEditingProfile}
                          placeholder="USA"
                        />
                      </div>
                    </div>

                    <div className="profile-stats">
                      <p>
                        <strong>Member Since:</strong>{" "}
                        {new Date(customer.createdAt).toLocaleDateString(
                          "en-US",
                          { month: "long", year: "numeric" },
                        )}
                      </p>
                      <p>
                        <strong>Total Bookings:</strong>{" "}
                        {customer.totalBookings || 0}
                      </p>
                      <p>
                        <strong>Total Spent:</strong>{" "}
                        {formatCurrency(customer.totalSpent || 0)}
                      </p>
                    </div>

                    <div
                      style={{
                        marginTop: "1rem",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "14px",
                        background: "#f8fafc",
                      }}
                    >
                      <h3
                        style={{
                          margin: "0 0 8px",
                          fontSize: "14px",
                          color: "#0f172a",
                        }}
                      >
                        Claim History
                      </h3>
                      <p
                        style={{
                          margin: "0 0 8px",
                          color: "#475569",
                          fontSize: "13px",
                          lineHeight: 1.5,
                        }}
                      >
                        If you used this email before creating your account, you can add older bookings and orders that are not yet linked.
                      </p>

                      {historyClaimPreview ? (
                        <>
                          <p
                            style={{
                              margin: "0 0 4px",
                              fontSize: "13px",
                              color: "#0f172a",
                            }}
                          >
                            Found bookings: {historyClaimPreview?.claimable?.bookings || 0}
                          </p>
                          <p
                            style={{
                              margin: "0 0 4px",
                              fontSize: "13px",
                              color: "#0f172a",
                            }}
                          >
                            Found orders: {historyClaimPreview?.claimable?.orders || 0}
                          </p>
                          <p
                            style={{
                              margin: "0 0 10px",
                              fontSize: "12px",
                              color: "#64748b",
                            }}
                          >
                            Status: {historyClaimPreview.claimStatus || "none"}
                          </p>

                          {(historyClaimPreview?.claimable?.total || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={handleConfirmHistoryClaim}
                              disabled={historyClaimLoading}
                              style={{
                                padding: "9px 12px",
                                borderRadius: "8px",
                                border: "none",
                                background: historyClaimLoading
                                  ? "#94a3b8"
                                  : "#1a659e",
                                color: "#fff",
                                fontWeight: 600,
                                cursor: historyClaimLoading
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              {historyClaimLoading
                                ? "Adding..."
                                : "Claim History"}
                            </button>
                          ) : historyClaimPreview.claimStatus === "claimed" ? (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#166534",
                                fontWeight: 600,
                              }}
                            >
                              History already claimed.
                            </div>
                          ) : (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                fontWeight: 500,
                              }}
                            >
                              No claimable history found.
                            </div>
                          )}

                          {historyClaimError && (
                            <div
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: "#b91c1c",
                              }}
                            >
                              {historyClaimError}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          Checking claimable history...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Booking Detail Modal */}
            {selectedBooking && (
              <div
                className="modal-overlay"
                onClick={() => setSelectedBooking(null)}
              >
                <div
                  className="modal-content booking-detail-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <div>
                      <div className="modal-kicker">Appointment Snapshot</div>
                      <h2>Booking Details</h2>
                    </div>
                    <button
                      className="modal-close"
                      onClick={() => setSelectedBooking(null)}
                    >
                      ×
                    </button>
                  </div>

                  <div className="modal-body booking-detail-body">
                    <div className="booking-detail-hero">
                      <div className="booking-detail-main">
                        <p className="booking-detail-service">
                          {selectedBooking.serviceId?.name ||
                            selectedBooking.serviceName ||
                            "Service"}
                        </p>
                        <p className="booking-detail-time">
                          {selectedBooking.appointmentDate &&
                          selectedBooking.startTime
                            ? new Date(
                                `${selectedBooking.appointmentDate}T${selectedBooking.startTime}:00`,
                              ).toLocaleString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })
                            : "Date not available"}
                        </p>
                      </div>
                      <span
                        className={`status-badge status-${selectedBooking.status}`}
                      >
                        {selectedBooking.status}
                      </span>
                    </div>

                    <div className="booking-detail-grid">
                      <div className="booking-detail-card">
                        <p className="booking-detail-label">Professional</p>
                        <p className="booking-detail-value">
                          {getBookingProfessionalName(selectedBooking)}
                        </p>
                      </div>
                      <div className="booking-detail-card">
                        <p className="booking-detail-label">Location</p>
                        <p className="booking-detail-value">
                          {selectedBooking.shopId?.name ||
                            selectedBooking.shopName ||
                            "Shop"}
                        </p>
                      </div>
                      <div className="booking-detail-card">
                        <p className="booking-detail-label">Duration</p>
                        <p className="booking-detail-value">
                          {selectedBooking.serviceId?.duration ||
                            selectedBooking.duration ||
                            "N/A"}{" "}
                          minutes
                        </p>
                      </div>
                      <div className="booking-detail-card">
                        <p className="booking-detail-label">Price</p>
                        <p className="booking-detail-value">
                          {formatCurrency(
                            getBookingServicePrice(selectedBooking),
                          )}
                        </p>
                      </div>
                    </div>

                    {(getBookingAddress(selectedBooking) ||
                      selectedBooking.shopId?.phone) && (
                      <div className="booking-detail-note">
                        {getBookingAddress(selectedBooking) && (
                          <div>
                            <strong>Address:</strong>{" "}
                            {getBookingAddress(selectedBooking)}
                          </div>
                        )}
                        {selectedBooking.shopId?.phone && (
                          <div>
                            <strong>Phone:</strong>{" "}
                            <a href={`tel:${selectedBooking.shopId.phone}`}>
                              {selectedBooking.shopId.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {(() => {
                      const supportContact =
                        getBookingSupportContact(selectedBooking);
                      return supportContact.phone ? (
                        <div className="booking-detail-note booking-detail-support">
                          <strong>Support Contact:</strong> Call{" "}
                          {supportContact.label.toLowerCase()} at{" "}
                          <a href={`tel:${supportContact.phone}`}>
                            {supportContact.phone}
                          </a>{" "}
                          for product + service issues.
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="modal-footer">
                    {selectedBooking.status === "confirmed" && (
                      <button
                        className="btn-danger"
                        onClick={() => {
                          setSelectedBooking(null);
                          handleCancelBooking(selectedBooking);
                        }}
                      >
                        Cancel Booking
                      </button>
                    )}
                    <button
                      className="btn-secondary"
                      onClick={() => setSelectedBooking(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Policy Modal */}
            {cancelBookingData && (
              <div
                className="modal-overlay"
                onClick={() => setCancelBookingData(null)}
              >
                <div
                  className="modal-content cancellation-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <div>
                      <div className="modal-kicker">Before You Cancel</div>
                      <h2>Cancellation Policy</h2>
                    </div>
                    <button
                      className="modal-close"
                      onClick={() => setCancelBookingData(null)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="cancellation-options">
                      {/* Group scope: which bookings are being cancelled? */}
                      {cancelTargets.length > 1 &&
                        (() => {
                          const allTicked =
                            cancelSelection.length === cancelTargets.length;
                          return (
                            <div
                              style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                marginBottom: "1rem",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  padding: "10px 12px",
                                  background: "#f9fafb",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                Who is this cancellation for?
                              </div>
                              {cancelTargets.map((target) => {
                                const isAnchor =
                                  target.isPrimaryGroupBooking === true;
                                const ticked = cancelSelection.includes(
                                  target._id,
                                );
                                // Ticking the anchor ticks everything, so a member is
                                // locked once the whole group is selected.
                                const locked = !isAnchor && allTicked;
                                const estimate = cancelRefundRows.find(
                                  (row) =>
                                    String(row.bookingId) ===
                                    String(target._id),
                                );
                                const price = estimate
                                  ? estimate.servicePrice
                                  : getBookingServicePrice(target);

                                return (
                                  <label
                                    key={target._id}
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      padding: "10px 12px",
                                      borderTop: "1px solid #f3f4f6",
                                      cursor: locked ? "default" : "pointer",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={ticked}
                                      disabled={locked}
                                      onChange={() =>
                                        handleCancelTargetToggle(target)
                                      }
                                      style={{ marginTop: "3px" }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      <span style={{ fontWeight: "600" }}>
                                        {isAnchor
                                          ? "Your booking"
                                          : target.groupMemberName ||
                                            "Group member"}
                                      </span>
                                      {" ­ "}
                                      {target.serviceName || "Service"}
                                      {target.startTime
                                        ? ` at ${formatTimeTz(target.startTime)}`
                                        : ""}
                                      {price
                                        ? ` · ${formatCurrency(price)}${
                                            estimate?.isPackageValue ? " package value" : ""
                                          }`
                                        : ""}
                                      {isAnchor && (
                                        <span
                                          style={{
                                            display: "block",
                                            color: "#991b1b",
                                            marginTop: "2px",
                                          }}
                                        >
                                          ⚠️ This is the anchor booking.
                                          Cancelling it cancels all{" "}
                                          {cancelTargets.length} bookings.
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                );
                              })}
                              {cancelSelection.length === 0 && (
                                <div
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "11px",
                                    color: "#991b1b",
                                    borderTop: "1px solid #f3f4f6",
                                  }}
                                >
                                  Select at least one booking to continue.
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {/* ── 22G: WHAT CANCELLING COSTS IN PACKAGE SESSIONS (user, 2026-10-14) ────────

                          *"the customer/guest should be warned that canceling the booking means the sessions
                          that were booked will be lost partially or completely. They have the option of redeeming
                          it by paying the difference depending on the cancellation time remaining."*

                          A warning without numbers is not a warning, and these numbers come from the SERVER —
                          the same function that prices the hold when the cancellation actually happens — so the
                          amount shown here is the amount charged. */}
                      {cancelPackagePreview && (
                        <div
                          style={{
                            border: "1px solid #fcd34d",
                            background: "#fffbeb",
                            borderRadius: "8px",
                            padding: "12px",
                            marginBottom: "1rem",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "#92400e",
                              marginBottom: "6px",
                            }}
                          >
                            ⚠️ {cancelPackagePreview.bookings} of these booking
                            {cancelPackagePreview.bookings === 1 ? "" : "s"} used
                            your package
                          </div>
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#78350f",
                              margin: "0 0 10px 0",
                              lineHeight: 1.5,
                            }}
                          >
                            {cancelPackagePreview.inTime
                              ? `Cancelling now returns ${cancelPackagePreview.units} session${
                                  cancelPackagePreview.units === 1 ? "" : "s"
                                } to your package and nothing is charged. You can keep them on the package, or let them go and take a refund.`
                              : `Cancelling now loses ${cancelPackagePreview.units} session${
                                  cancelPackagePreview.units === 1 ? "" : "s"
                                } from your package. You can keep them by paying the late-cancellation charge${
                                  Number(cancelPackagePreview.refundAmount) > 0
                                    ? ", or take a refund for the part that is refundable."
                                    : ", or let them go — nothing is left to refund at this notice."
                                }`}
                          </p>

                          {/* ── THE CHOICE IS OFFERED AT EVERY BAND (user, 2026-10-17) ─────────────
                              *"when the cancellation charge is 0%, the losse or keep option is not displayed,
                              neither is refund option shown, can it be uniform. The only time there's no refund
                              option is when its 100% charge."* So this block is no longer conditional: at 0%
                              KEEP costs nothing and LOSE refunds the whole net; only the wording changes. */}
                          <>
                              {/* ── THE LOSE CHOICE IS ALWAYS OFFERED (user, 2026-10-17) ───────────────
                                  It used to be gated on `refundEligible`, so at a 100% late-cancel notice
                                  the dialog showed only "keep" — the choice was one-sided, and the
                                  customer's only way out was to pay. Their words: *“The keep or loose is
                                  one sided, meaning they only have one choice and that is to keep.”*
                                  The radio is always rendered; only the wording changes when there is
                                  nothing to refund. */}
                              <label
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "flex-start",
                                  fontSize: "11px",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  background:
                                    cancelPackageChoice === "refund"
                                      ? "#fef3c7"
                                      : "transparent",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="packageCancellationChoice"
                                  checked={cancelPackageChoice === "refund"}
                                  onChange={() => setCancelPackageChoice("refund")}
                                  style={{ marginTop: "2px" }}
                                />
                                <span style={{ color: "#78350f", lineHeight: 1.5 }}>
                                  {Number(cancelPackagePreview.refundAmount) > 0 ? (
                                    <>
                                      <strong>
                                        Take the refund — $
                                        {Number(cancelPackagePreview.refundAmount).toFixed(2)}
                                      </strong>
                                      <br />
                                      The sessions are lost and this much goes back to
                                      your card.
                                      {/* THE DEDUCTION IS SHOWN, NOT JUST APPLIED (user, 2026-10-17):
                                          "the amount eligible for refund should be shown as an option
                                          less stripe's fee". */}
                                      {Number(cancelPackagePreview.processingFee) > 0 && (
                                        <>
                                          <br />
                                          <span style={{ color: "#92400e" }}>
                                            Value $
                                            {Number(
                                              cancelPackagePreview.basis || 0,
                                            ).toFixed(2)}{" "}
                                            − card processing fee $
                                            {Number(
                                              cancelPackagePreview.processingFee,
                                            ).toFixed(2)}
                                            {Number(cancelPackagePreview.feePercent) > 0 &&
                                              Number(cancelPackagePreview.redeemAmount) > 0 &&
                                              ` − late-cancellation share $${Number(
                                                cancelPackagePreview.redeemAmount,
                                              ).toFixed(2)}`}
                                          </span>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <strong>Let the sessions go — nothing refunded</strong>
                                      <br />
                                      Nothing is left to refund at this notice, so the
                                      sessions are simply given up.
                                    </>
                                  )}
                                </span>
                              </label>

                              <label
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "flex-start",
                                  fontSize: "11px",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  background:
                                    cancelPackageChoice === "redeem"
                                      ? "#fef3c7"
                                      : "transparent",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="packageCancellationChoice"
                                  checked={cancelPackageChoice === "redeem"}
                                  onChange={() => setCancelPackageChoice("redeem")}
                                  style={{ marginTop: "2px" }}
                                />
                                <span style={{ color: "#78350f", lineHeight: 1.5 }}>
                                  {cancelPackagePreview.inTime ? (
                                    <>
                                      <strong>
                                        Keep the sessions — back on your package
                                      </strong>
                                      <br />
                                      Nothing is charged inside the free cancellation
                                      window.
                                    </>
                                  ) : (
                                    <>
                                      <strong>
                                        Keep the sessions — pay $
                                        {cancelPackagePreview.redeemAmount.toFixed(2)}
                                      </strong>
                                      <br />
                                      Pay now, or later when you book with this package.
                                      They go back on the package once it is paid.
                                    </>
                                  )}
                                </span>
                              </label>

                              {/* ── PAY NOW OR PAY LATER (user, 2026-10-14) ─────────────────────────────
                                  The sub-option they asked for. Only shown when they chose to KEEP the
                                  sessions AND there is something to pay: at 0% the sessions simply go
                                  back, and on the refund side nothing is payable at all. */}
                              {!cancelPackagePreview.inTime && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  paddingTop: "8px",
                                  borderTop: "1px dashed #fcd34d",
                                  fontSize: "11px",
                                  color: "#78350f",
                                }}
                              >
                                <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                                  When would you like to pay?
                                </div>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    padding: "4px 0",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="cancelPayTiming"
                                    checked={cancelPayTiming === "now"}
                                    onChange={() => setCancelPayTiming("now")}
                                  />
                                  <span>
                                    <strong>Pay now</strong> — a payment window opens as
                                    the cancellation completes.
                                  </span>
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    padding: "4px 0",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="cancelPayTiming"
                                    checked={cancelPayTiming === "later"}
                                    onChange={() => setCancelPayTiming("later")}
                                  />
                                  <span>
                                    <strong>Pay later</strong> — the offer waits on your
                                    booking page, and your other sessions keep working.
                                  </span>
                                </label>
                              </div>
                              )}

                              {/* ── PAY FOR THIS CANCELLATION, OR FOR EVERYTHING HELD (user, 2026-10-16) ──
                                  *"include the system searching if there are other cancelled session(s)
                                  associated with the package number and give the customer/guest the option of
                                  paying altogether"*. Only shown when the package is already holding sessions
                                  from EARLIER cancellations — otherwise there is no second option to offer. The
                                  two figures come from the server (the warning carries the package's own held
                                  total) and whichever is chosen is what gets charged, because the ids that were
                                  displayed are the ids that are priced. */}
                              {(cancelBookingData.packageCancellation?.otherHeldUnits || 0) > 0 &&
                                !cancelPackagePreview.inTime &&
                                cancelPayTiming === "now" && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                      borderTop: "1px dashed #fcd34d",
                                      fontSize: "11px",
                                      color: "#78350f",
                                    }}
                                  >
                                    <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                                      What would you like to pay for?
                                    </div>
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "flex-start",
                                        padding: "4px 0",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="radio"
                                        name="cancelPayScope"
                                        checked={cancelPayScope === "this"}
                                        onChange={() => setCancelPayScope("this")}
                                        style={{ marginTop: "2px" }}
                                      />
                                      <span>
                                        <strong>
                                          Just this cancellation — $
                                          {cancelPackagePreview.redeemAmount.toFixed(2)}
                                        </strong>
                                        <br />
                                        {cancelPackagePreview.units} session(s). Your other held
                                        sessions stay as they are.
                                      </span>
                                    </label>
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "flex-start",
                                        padding: "4px 0",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="radio"
                                        name="cancelPayScope"
                                        checked={cancelPayScope === "all"}
                                        onChange={() => setCancelPayScope("all")}
                                        style={{ marginTop: "2px" }}
                                      />
                                      <span>
                                        <strong>
                                          Everything held on this package — $
                                          {(
                                            cancelPackagePreview.redeemAmount +
                                            Number(
                                              cancelBookingData.packageCancellation
                                                ?.otherHeldAmount || 0,
                                            )
                                          ).toFixed(2)}
                                        </strong>
                                        <br />
                                        {cancelPackagePreview.units +
                                          Number(
                                            cancelBookingData.packageCancellation?.otherHeldUnits ||
                                              0,
                                          )}{" "}
                                        session(s) in one payment — including the {
                                          cancelBookingData.packageCancellation?.otherHeldUnits
                                        }{" "}
                                        held from an earlier cancellation.
                                      </span>
                                    </label>
                                  </div>
                                )}
                            </>
                        </div>
                      )}

                      <h3 style={{ fontSize: "12px", marginBottom: "1rem" }}>
                        Choose Cancellation Method:
                      </h3>
                      <div className="cancellation-options-grid">
                        {/* Option 1: Contact Shop */}
                        <div className="cancellation-option">
                          <h4 style={{ fontSize: "12px" }}>
                            Option 1: Contact Shop Directly (Recommended)
                          </h4>
                          <p style={{ fontSize: "11px", marginBottom: "1rem" }}>
                            Call directly to cancel your appointment or handle
                            product + service refund questions quickly while the
                            in-app return flow is being finalized.
                          </p>
                          {(() => {
                            const supportContact = getBookingSupportContact(
                              cancelBookingData.booking,
                            );
                            return supportContact.phone ? (
                              <p
                                style={{
                                  fontSize: "11px",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                <strong>Call {supportContact.label}:</strong>{" "}
                                <a href={`tel:${supportContact.phone}`}>
                                  {supportContact.phone}
                                </a>
                              </p>
                            ) : null;
                          })()}
                          <div className="shop-contact-info">
                            <p>
                              <strong>Shop:</strong>{" "}
                              {getBookingShopName(cancelBookingData.booking)}
                            </p>
                            {cancelBookingData.booking.shopId?.phone && (
                              <p>
                                <strong>Phone:</strong>{" "}
                                <a
                                  href={`tel:${cancelBookingData.booking.shopId.phone}`}
                                >
                                  {cancelBookingData.booking.shopId.phone}
                                </a>
                              </p>
                            )}
                            {cancelBookingData.booking.shopId?.address && (
                              <p>
                                <strong>Address:</strong>{" "}
                                {cancelBookingData.booking.shopId.address}
                              </p>
                            )}
                            <p>
                              <strong>Professional:</strong>{" "}
                              {getBookingProfessionalName(
                                cancelBookingData.booking,
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Option 2: Self-Cancel with Fee */}
                        <div className="cancellation-option">
                          <h4 style={{ fontSize: "12px" }}>
                            Option 2: Cancel Now (Automatic)
                          </h4>
                          <p style={{ fontSize: "11px" }}>
                            Based on our cancellation policy:
                          </p>
                          <div className="cancellation-fee-info">
                            <p>
                              <strong>Time until appointment:</strong>{" "}
                              {cancelPreview?.hoursUntilAppointment ?? 0}{" "}
                              hours
                            </p>
                            {/* ── A PACKAGE BOOKING SHOWS ITS PACKAGE VALUE (user, 2026-10-17) ──────────────
                                `cancelPreview.servicePrice` is the CARD calculator's figure. On a package booking the
                                card paid nothing for the service, so it reads $0 (or the listed price) beside rows
                                that correctly say "$24.00 package value". When every ticked booking is a package
                                row, the price shown is the sum of those rows' own `basis` — the same numbers on the
                                rows — and the label says which figure it is. A MIXED selection keeps the card
                                figure, because splitting one total across two pricing models is how a screen starts
                                quoting something the server will not charge. */}
                            {(() => {
                              const selectedPackageRows = cancelTargets
                                .filter((target) =>
                                  cancelSelection.includes(target._id),
                                )
                                .map(
                                  (target) =>
                                    cancelBookingData?.packageCancellations?.[
                                      String(target._id)
                                    ],
                                )
                                .filter(Boolean);
                              const allPackage =
                                selectedPackageRows.length > 0 &&
                                selectedPackageRows.length ===
                                  Math.max(1, cancelSelection.length);
                              const shownValue = allPackage
                                ? Math.round(
                                    selectedPackageRows.reduce(
                                      (sum, row) => sum + (Number(row.basis) || 0),
                                      0,
                                    ) * 100,
                                  ) / 100
                                : Number(cancelPreview?.servicePrice) || 0;
                              return (
                                <p>
                                  <strong>
                                    {allPackage ? "Package value" : "Service Price"}
                                    {cancelPreview?.isGroup
                                      ? " (selected bookings):"
                                      : ":"}
                                  </strong>{" "}
                                  {formatCurrency(shownValue)}
                                </p>
                              );
                            })()}
                            {cancelPreview?.feeKnown === false && (
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#9a3412",
                                  marginTop: "0.5rem",
                                }}
                              >
                                The cancellation fee for this appointment is
                                confirmed when you submit.
                              </p>
                            )}
                            {cancelPreview?.feeKnown &&
                              (cancelPreview?.feePercent || 0) > 0 && (
                                <>
                                  <p className="fee-warning">
                                    <strong>
                                      Cancellation Fee
                                      {cancelPreview.feePercent !== null
                                        ? ` (${cancelPreview.feePercent}%)`
                                        : ""}
                                      :
                                    </strong>{" "}
                                    {formatCurrency(
                                      cancelPreview.cancellationFee,
                                    )}
                                  </p>
                                  <p
                                    style={{
                                      fontSize: "11px",
                                      color: "#dc3545",
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    ⚠️ The fee applies to{" "}
                                    {cancelPreview?.isGroup
                                      ? "the bookings you selected"
                                      : "this booking"}{" "}
                                    at the rate for how soon the appointment
                                    starts.
                                  </p>
                                </>
                              )}
                            {cancelPreview?.feeKnown &&
                              (cancelPreview?.feePercent || 0) === 0 && (
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#28a745",
                                  }}
                                >
                                  ✓ No cancellation fee at this notice
                                </p>
                              )}
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#9a3412",
                                marginTop: "0.75rem",
                                lineHeight: "1.5",
                              }}
                            >
                              {NON_REFUNDABLE_PROCESSING_FEE_REFUND_NOTE}
                            </p>
                          </div>

                          <div
                            className="policy-reminder"
                            style={{
                              marginTop: "1rem",
                              padding: "1rem",
                              background: "#f8fafc",
                              borderRadius: "8px",
                            }}
                          >
                            <p style={{ fontSize: "11px", margin: 0 }}>
                              <strong>Cancellation Policy:</strong>
                            </p>
                            <ul
                              style={{
                                fontSize: "11px",
                                margin: "0.5rem 0",
                                paddingLeft: "1.5rem",
                              }}
                            >
                              {(cancelPreview?.policyBullets || []).map(
                                (row) => (
                                  <li key={row.label}>{row.label}</li>
                                ),
                              )}
                            </ul>
                            <p
                              style={{
                                fontSize: "11px",
                                margin: 0,
                                color: "#9a3412",
                                lineHeight: "1.5",
                              }}
                            >
                              {NON_REFUNDABLE_PROCESSING_FEE_NOTICE}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn-danger"
                      onClick={confirmCancellation}
                      disabled={
                        (cancelTargets.length > 1 &&
                          cancelSelection.length === 0) ||
                        // KEEP OR LOSE MUST BE CHOSEN DELIBERATELY (user, 2026-10-17). The radios used to default
                        // to "refund", so a customer who never read the block had already chosen the outcome that
                        // loses their sessions. Nothing is pre-selected now, and this button says what is missing.
                        // THE CHOICE EXISTS AT EVERY BAND, 0% included (2026-10-17), so it is no longer gated on
                        // `inTime` — that gate is what left an in-time cancellation on a disabled button with no
                        // radios on screen to satisfy it.
                        (cancelBookingData?.packageCancellation &&
                          !cancelPackageChoice)
                      }
                    >
                      {/* The button says what pressing it will do, including which side of the package choice is
                          ticked — "Confirm Cancellation" alongside a "keep my sessions by paying" radio would
                          read as cancelling only, and the customer would be surprised by a payment page. */}
                      {cancelBookingData?.packageCancellation && !cancelPackageChoice
                        ? "Choose above: keep or lose your sessions"
                        : cancelBookingData?.packageCancellation &&
                            cancelPackageChoice === "redeem"
                          ? cancelBookingData.packageCancellation.inTime
                            ? "Cancel and return the sessions to my package"
                            : `Cancel and pay $${Number(
                                cancelBookingData.packageCancellation.redeemAmount,
                              ).toFixed(2)} to keep them`
                          // The lose side is only a "refund" when there is money to give back; at a 100% notice
                          // it forfeits the sessions, and "Cancel and refund $0.00" would be a lie (2026-10-17).
                          : cancelBookingData?.packageCancellation
                            ? Number(cancelBookingData.packageCancellation.refundAmount) > 0
                              ? `Cancel and refund $${Number(
                                  cancelBookingData.packageCancellation.refundAmount,
                                ).toFixed(2)}`
                              : "Cancel and let the sessions go"
                            : "Confirm Cancellation"}
                      {cancelBookingData?.packageCancellation?.inTime
                        ? ""
                        : (cancelPreview?.feePercent || 0) > 0 &&
                          ` (${cancelPreview.feePercent}% fee)`}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setCancelBookingData(null)}
                    >
                      Keep Booking
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Order Tracking Modal */}
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default CustomerDashboard;
